import { useState, useEffect } from "react";
import {
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  Card,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { FormAutoComplete } from "@/components/form/FormAutoComplete";
import { useQuestionnaireResponses } from "../../../../hooks/useQuestionnaireResponses";
import { useQuestionnaires } from "../../../../hooks/useQuestionnaires";
import { useMedicalCareContext } from "../../../../contexts/MedicalCareContext";
import { QuestionnaireHistoryView } from "./QuestionnaireHistoryView";
import dayjs from "dayjs";
import { scrollbarStyle } from "../scrollbarStyle";
import { useLocation } from "react-router-dom";
import { useQuestionnaireContext } from "@/contexts/QuestionnaireContext";

interface QuestionnairesHistoryProps {
  selectedQuestionnaire?: any;
  selectedQuestionnaireId?: string;
  setSelectedQuestionnaireId?: (questionnaireId: string) => void;
  onRefresh?: () => void;
}

export function QuestionnairesHistory({
  selectedQuestionnaire: propSelectedQuestionnaire,
  selectedQuestionnaireId,
  setSelectedQuestionnaireId,
  onRefresh,
}: QuestionnairesHistoryProps) {
  const { appointment } = useMedicalCareContext();
  const { state } = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const patientIdFromAppointment = appointment?.participant
    ?.find((participant: any) => participant.reference?.includes("Patient"))
    ?.reference?.split("/")[1];
  const patientIdFromState = state?.encounterReference?.subject;

  const patientId = patientIdFromAppointment || patientIdFromState;

  const {
    questionnaires,
    loading: questionnairesLoading,
    fetchQuestionnaires,
  } = useQuestionnaires({
    count: 100,
    status: "active",
    tag: "sleep",
  });

  const {
    responses,
    loading: responsesLoading,
    error,
    getResponsesByQuestionnaire,
    fetchResponses,
  } = useQuestionnaireResponses({
    patientId: patientId?.toString(),
    tag: "questionnaires",
  });

  const { selectedHistoryQuestionnaireId, setSelectedHistoryQuestionnaireId } =
    useQuestionnaireContext();

  const [questionnaireResponses, setQuestionnaireResponses] = useState<any[]>(
    []
  );

  useEffect(() => {
    if (!questionnaires.length && !questionnairesLoading) {
      fetchQuestionnaires();
    }
  }, []);

  const uniqueQuestionnaires = responses.reduce((acc: any[], response: any) => {
    const questionnaireRef = response.resource.questionnaire;
    if (questionnaireRef && !acc.find((q) => q?.id === questionnaireRef)) {
      const questionnaireId = questionnaireRef.split("/")[1];

      const questionnaireData = questionnaires?.find(
        (q: any) => q?.id === questionnaireId
      );

      const title =
        questionnaireData?.title ||
        questionnaireData?.name ||
        `Questionário ${questionnaireId}`;

      acc.push({
        id: questionnaireRef,
        title: title,
        value: questionnaireRef,
        label: title,
        lastResponseDate: response.resource.authored,
      });
    }
    return acc;
  }, []);

  const sortedQuestionnaires = uniqueQuestionnaires.sort((a, b) =>
    dayjs(b.lastResponseDate).diff(dayjs(a.lastResponseDate))
  );

  useEffect(() => {
    const preferredId = selectedHistoryQuestionnaireId;
    if (preferredId) {
      setSelectedQuestionnaireId?.(preferredId);
    } else if (sortedQuestionnaires.length > 0 && !selectedQuestionnaireId) {
      setSelectedQuestionnaireId?.(sortedQuestionnaires?.[0]?.id);
    }
  }, [
    sortedQuestionnaires,
    selectedQuestionnaireId,
    selectedHistoryQuestionnaireId,
  ]);

  useEffect(() => {
    if (selectedQuestionnaireId && responses.length > 0) {
      const filteredResponses = getResponsesByQuestionnaire(
        selectedQuestionnaireId
      );

      const sortedResponses = filteredResponses.sort((a, b) =>
        dayjs(b.resource.authored).diff(dayjs(a.resource.authored))
      );

      setQuestionnaireResponses(sortedResponses);
    } else {
      setQuestionnaireResponses([]);
    }
  }, [selectedQuestionnaireId, responses, getResponsesByQuestionnaire]);

  useEffect(() => {
    if (selectedHistoryQuestionnaireId && selectedQuestionnaireId) {
      fetchResponses();
    }
  }, [selectedHistoryQuestionnaireId, fetchResponses, selectedQuestionnaireId]);

  const selectedQuestionnaire = sortedQuestionnaires?.find(
    (questionnaireRef) => questionnaireRef?.id === selectedQuestionnaireId
  );

  const loading = questionnairesLoading || responsesLoading;

  interface FormValues {
    selectedQuestionnaire: { value: string; label: string } | null;
  }

  const { control, setValue } = useForm<FormValues>({
    defaultValues: {
      selectedQuestionnaire: null,
    },
  });

  useEffect(() => {
    if (selectedQuestionnaireId && sortedQuestionnaires.length > 0) {
      const selectedOption = {
        value: selectedQuestionnaireId,
        label:
          sortedQuestionnaires?.find((q) =>
            q?.id?.includes(selectedQuestionnaireId)
          )?.title || "",
      };
      setValue("selectedQuestionnaire", selectedOption);
    }
  }, [selectedQuestionnaireId, sortedQuestionnaires, setValue]);

  if (loading) {
    return (
      <Stack height="100%" justifyContent="center" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          Carregando histórico...
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack height="100%" sx={{ ...scrollbarStyle }}>
      {!error && (
        <>
          <Stack
            width="80%"
            mx="auto"
            p={2}
            borderBottom="1px solid"
            borderColor="divider"
          >
            {!loading && sortedQuestionnaires?.length > 0 && (
              <FormAutoComplete
                key={selectedQuestionnaireId}
                name="selectedQuestionnaire"
                control={control}
                label="Selecionar Questionário"
                selectOptions={sortedQuestionnaires.map(
                  (questionnaire: any) => ({
                    value: questionnaire?.id,
                    label: questionnaire?.title,
                  })
                )}
                defaultValue={control._formValues.selectedQuestionnaire}
                auxOnChange={(selectedOption: any) => {
                  if (
                    selectedOption &&
                    selectedQuestionnaireId !== selectedOption.value
                  ) {
                    setSelectedQuestionnaireId?.(selectedOption.value);
                    setSelectedHistoryQuestionnaireId(selectedOption.value);
                  }
                }}
                noOptionsText="Nenhum questionário com respostas encontrado"
              />
            )}
          </Stack>

          <Stack flex={1} overflow="auto" p={2}>
            {!loading && questionnaireResponses.length > 0 && (
              <>
                <QuestionnaireHistoryView
                  responses={questionnaireResponses}
                  questionnaire={selectedQuestionnaire || null}
                  isMobile={isMobile}
                />
              </>
            )}
          </Stack>
        </>
      )}
      {loading && (
        <Stack height="100%" justifyContent="center" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Carregando histórico...
          </Typography>
        </Stack>
      )}

      {error && (
        <Stack height="100%" justifyContent="center" alignItems="center">
          <Typography variant="body2" color="error">
            Erro ao carregar histórico: {error}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}
